
function getMsHealthMetric(...keys) {
  if (!weatherData().msHealth) {
    return null;
  }

  return keys
    .map((key) => weatherData().msHealth[key])
    .find((metric) => metric);
}

function weatherData() {
  return typeof twigData === 'undefined' || !twigData ? {} : twigData;
}

// Only finite numeric values are data. In particular, false, whitespace and
// placeholders must not be silently converted to zero.
export function hasWeatherNumber(value) {
  return (typeof value === 'number' || typeof value === 'string')
    && String(value).trim() !== '' && Number.isFinite(Number(value));
}

function isZeroNumericValue(value) {
  return hasWeatherNumber(value) && Number(value) === 0;
}

export function hasWeatherText(value) {
  return (typeof value === 'string' || typeof value === 'number')
    && !['', '-', '--', 'close', 'null', 'undefined', 'Sem dados'].includes(String(value).trim());
}

export function emitWeatherError(name) {
  const context = weatherData();
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'error_hit', name,
    page: context.forecastPage || window.location?.pathname,
    city_id: window.pageData?.infoCity?.idcity });
}

export function errorHitMsHealth() {

  if (!weatherData().msHealth?.airQuality) {
    emitWeatherError('data_aggregator_forecast_air-quality_daily');
  }
  if (!getMsHealthMetric('flu_cold', 'flu')) {
    emitWeatherError('data_aggregator_forecast_flu_daily');
  }
  if (!weatherData().msHealth?.vitaminD) {
    emitWeatherError('data_aggregator_forecast_vitamin-d_daily');
  }
  if (!weatherData().msHealth?.mosquito) {
    emitWeatherError('data_aggregator_forecast_mosquito_daily');
  }
  if (!getMsHealthMetric('iuv', 'radiation')) {
    emitWeatherError('data_aggregator_forecast_uv_daily');
  }
  if (!weatherData().msHealth?.skindryness) {
    emitWeatherError('data_aggregator_forecast_skin-dryness_daily');
  }
}

export function errorHitNearbyCities() {

  (Array.isArray(weatherData().nearbyCities) ? weatherData().nearbyCities : []).forEach((city, index) => {
    if (!hasWeatherNumber(city?.momentTemperature)) {
      emitWeatherError(`data_aggregator_now_temperature_city_${index + 1}`);
    }
    if (!hasWeatherNumber(city?.momentSensation)) {
      emitWeatherError(`data_aggregator_now_thermal-sensation_city_${index + 1}`);
    }

  })
}

export function errorHitNearbyAirports() {

  (Array.isArray(weatherData().nearbyAirports) ? weatherData().nearbyAirports : []).forEach((airport, index) => {
    if (!hasWeatherNumber(airport?.weather?.temperature)) {
      emitWeatherError(`data_aggregator_now_temperature_airport_${index + 1}`);
    }
    if (!hasWeatherNumber(airport?.weather?.sensation)) {
      emitWeatherError(`data_aggregator_now_thermal-sensation_airport_${index + 1}`);
    }
  })
}

export function errorHitClimatology() {

  if (!hasWeatherNumber(weatherData().volumeOfRain?.averageMonthlyRainfall)) {
    emitWeatherError('data_aggregator_climatology_precipitation');
  }
}

export function errorHitObserved() {

  if (!hasWeatherNumber(weatherData().volumeOfRain?.sumRainInTheMonth)) {
    emitWeatherError('data_aggregator_observed_precipitation');
  }
}

export function errorHitForecastDaily() {

  (Array.isArray(weatherData().dailyForecast) ? weatherData().dailyForecast : []).forEach((forecast, index) => {
    if (!hasWeatherNumber(forecast?.thermalSensation?.thermalSensationMin)
      || !hasWeatherNumber(forecast?.thermalSensation?.thermalSensationMax)) {
      emitWeatherError(`data_aggregator_forecast_thermal-sensation_daily_${index + 1}`);
    }
    if (!hasWeatherNumber(forecast?.gust?.speed)) {
      emitWeatherError(`data_aggregator_forecast_gust_daily_${index + 1}`);
    }
    if (!hasWeatherText(forecast?.textIcon?.text?.pt)) {
      emitWeatherError(`data_aggregator_forecast_condition_daily_${index + 1}`);
    }
    if (!hasWeatherText(forecast?.moonPhases?.phase)) {
      emitWeatherError(`data_aggregator_forecast_moon-phase_daily_${index + 1}`);
    }
    if (!hasWeatherText(forecast?.sun?.sunset) || !hasWeatherText(forecast?.sun?.sunshine)) {
      emitWeatherError(`data_aggregator_forecast_sun_daily_${index + 1}`);
    }
    if (!hasWeatherNumber(forecast?.humidity?.min) || !hasWeatherNumber(forecast?.humidity?.max)) {
      emitWeatherError(`data_aggregator_forecast_humidity_daily_${index + 1}`);
    }
    if (!hasWeatherNumber(forecast?.wind?.avgVelocity)) {
      emitWeatherError(`data_aggregator_forecast_wind-velocity_daily_${index + 1}`);
    }
    if (!hasWeatherText(forecast?.wind?.direction) || !hasWeatherNumber(forecast?.wind?.direction_degrees)) {
      emitWeatherError(`data_aggregator_forecast_wind-direction_daily_${index + 1}`);
    }
    if (!hasWeatherNumber(forecast?.temperature?.min) || !hasWeatherNumber(forecast?.temperature?.max)) {
      emitWeatherError(`data_aggregator_forecast_temperature_daily_${index + 1}`);
    }
    if (!hasWeatherNumber(forecast?.rain?.precipitation)) {
      emitWeatherError(`data_aggregator_forecast_precipitation_daily_${index + 1}`);
    }
    const hidesRainProbabilityByDesign = isZeroNumericValue(forecast?.rain?.precipitation);

    if (!hidesRainProbabilityByDesign && !hasWeatherNumber(forecast?.rain?.probability)) {
      emitWeatherError(`data_aggregator_forecast_precipitation-probability_daily_${index + 1}`);
    }
    if (!hasWeatherText(forecast?.textIcon?.icon?.day)) {
      emitWeatherError(`data_aggregator_forecast_icon_daily_${index + 1}`);
    }
    if (!['afternoon', 'dawn', 'morning', 'night'].every(period => hasWeatherText(forecast?.textIcon?.icon?.[period]))) {
      emitWeatherError(`data_aggregator_forecast_icon_period_${index + 1}`);
    }
  });
}

export function errorHitForecastHourly() {

  Object.keys(weatherData().hourlyForecast || {}).forEach((day, dayIndex) => {
    if (dayIndex >= 3) return;
    
    (Array.isArray(weatherData().hourlyForecast[day]) ? weatherData().hourlyForecast[day] : []).forEach((hour, hourIndex) => {

      if (!hasWeatherNumber(hour?.temperature?.temperature)) {
        emitWeatherError(`data_aggregator_forecast_temperature_hourly_${dayIndex + 1}-${hourIndex}`);
      }
      
      if (!hasWeatherNumber(hour?.pressure?.pressure)) {
        emitWeatherError(`data_aggregator_forecast_pressure_hourly_${dayIndex + 1}-${hourIndex}`);
      }

      if (!hasWeatherNumber(hour?.humidity?.relativeHumidity)) {
        emitWeatherError(`data_aggregator_forecast_humidity_hourly_${dayIndex + 1}-${hourIndex}`);
      }

      if (!hasWeatherNumber(hour?.wind?.velocity)) {
        emitWeatherError(`data_aggregator_forecast_wind-velocity_hourly_${dayIndex + 1}-${hourIndex}`);
      }

      if (!hasWeatherText(hour?.wind?.direction) || !hasWeatherNumber(hour?.wind?.direction_degrees)) {
        emitWeatherError(`data_aggregator_forecast_wind-direction_hourly_${dayIndex + 1}-${hourIndex}`);
      }

      if (!hasWeatherNumber(hour?.rain?.precipitation)) {
        emitWeatherError(`data_aggregator_forecast_precipitation_hourly_${dayIndex + 1}-${hourIndex}`);
      }
    })
  })
}

export function errorHitFifteenDays() {

  if (!weatherData().msHealth) {
    emitWeatherError('data_aggregator_forecast_health');
  } else {
    errorHitMsHealth()
  }

  if (!weatherData().volumeOfRain) {
    emitWeatherError('data_aggregator_climatology');
  } else {
    errorHitClimatology();
    errorHitObserved();
  }

  if (!Array.isArray(weatherData().dailyForecast) || !weatherData().dailyForecast.length) {
    emitWeatherError('data_aggregator_forecast_daily');
  } else {
    errorHitForecastDaily()
  }

  if (!weatherData().hourlyForecast || typeof weatherData().hourlyForecast !== 'object'
    || !Object.values(weatherData().hourlyForecast).some(rows => Array.isArray(rows) && rows.length)) {
    emitWeatherError('data_aggregator_forecast_hourly');
  } else {
    errorHitForecastHourly()
  }

  if (!Array.isArray(weatherData().nearbyCities) || !weatherData().nearbyCities.length) {
    emitWeatherError('data_aggregator_now_nearby-cities');
  } else {
    errorHitNearbyCities()
  }

  if (!Array.isArray(weatherData().nearbyAirports) || !weatherData().nearbyAirports.length) {
    emitWeatherError('data_aggregator_now_nearby-airports');
  } else {
    errorHitNearbyAirports()
  }

}
